import { message } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutSite } from '../components/layout';
import { get_config, put_config } from '../services/api.service';
import './admin.scss';

/* -------------------------------------------------------------------------------- *\
|                               SourceTable
\* -------------------------------------------------------------------------------- */
interface SourceTable
{
    id: string;
    name: string;
    sics: string[];
}

/* -------------------------------------------------------------------------------- *\
|                               SicsInput
\* -------------------------------------------------------------------------------- */
const SicsInput: FC<{ sics: string[], onChange: (sics: string[]) => void }> = ({ sics, onChange }) =>
{
    const [raw, setRaw] = useState(sics.join(', '));

    useEffect(() => { setRaw(sics.join(', ')); }, [sics]);

    const commit = () =>
    {
        onChange(raw.split(',').map(s => s.trim().toUpperCase()).filter(s => s));
    }

    return (
        <input
            className="admin__input"
            value={raw}
            onChange={e => setRaw(e.target.value)}
            onBlur={commit}
        />
    );
}

/* -------------------------------------------------------------------------------- *\
|                               AdminPage
\* -------------------------------------------------------------------------------- */
export const AdminPage: FC = () =>
{
    const [sources, setSources] = useState<SourceTable[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    /* -------------------------------------------------------------------------------- *\
    |                               load
    \* -------------------------------------------------------------------------------- */
    useEffect(() =>
    {
        get_config()
            .then(data => { setSources(data.source_table); setLoading(false); })
            .catch(error =>
            {
                if (error.response?.status === 403) navigate('/');
                else message.error(error.message);
                setLoading(false);
            });
        // eslint-disable-next-line
    }, []);

    /* -------------------------------------------------------------------------------- *\
    |                               save
    \* -------------------------------------------------------------------------------- */
    const save = () =>
    {
        setSaving(true);
        put_config({ source_table: sources })
            .then(() => { message.success('Saved'); setSaving(false); })
            .catch(error => { message.error(error.message); setSaving(false); });
    }

    /* -------------------------------------------------------------------------------- *\
    |                               updateSics
    \* -------------------------------------------------------------------------------- */
    const updateSics = (index: number, sics: string[]) =>
    {
        const updated = [...sources];
        updated[index] = { ...updated[index], sics };
        setSources(updated);
    }

    /* -------------------------------------------------------------------------------- *\
    |                               updateName
    \* -------------------------------------------------------------------------------- */
    const updateName = (index: number, value: string) =>
    {
        const updated = [...sources];
        updated[index] = { ...updated[index], name: value };
        setSources(updated);
    }

    /* -------------------------------------------------------------------------------- *\
    |                               updateId
    \* -------------------------------------------------------------------------------- */
    const updateId = (index: number, value: string) =>
    {
        const updated = [...sources];
        updated[index] = { ...updated[index], id: value };
        setSources(updated);
    }

    /* -------------------------------------------------------------------------------- *\
    |                               addSource
    \* -------------------------------------------------------------------------------- */
    const addSource = () =>
    {
        setSources([...sources, { id: '', name: '', sics: [] }]);
    }

    /* -------------------------------------------------------------------------------- *\
    |                               removeSource
    \* -------------------------------------------------------------------------------- */
    const removeSource = (index: number) =>
    {
        setSources(sources.filter((_, i) => i !== index));
    }

    if (loading) return <LayoutSite><div className="admin">Loading...</div></LayoutSite>;

    return (
        <LayoutSite>
            <div className="admin">
                <div className="admin__actions">
                    <button className="admin__btn" onClick={addSource}>+ Add</button>
                    <button className="admin__btn admin__btn--primary" onClick={save} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
                <table className="admin__table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>SICs (comma-separated)</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sources.map((source, index) => (
                            <tr key={index}>
                                <td><input className="admin__input" value={source.id} onChange={e => updateId(index, e.target.value)} /></td>
                                <td><input className="admin__input" value={source.name} onChange={e => updateName(index, e.target.value)} /></td>
                                <td><SicsInput sics={source.sics} onChange={sics => updateSics(index, sics)} /></td>
                                <td><button className="admin__btn admin__btn--danger" onClick={() => removeSource(index)}>✕</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </LayoutSite>
    );
}
